import { createContext, useContext, useState, useEffect } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const currentToken = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {}
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setToken(currentToken);
      } else {
        setUser(null);
        localStorage.removeItem('token');
        setToken(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      let msg = 'Login failed';
      try { const error = await response.json(); msg = error.detail || msg; } catch {}
      throw new Error(msg);
    }

    const data = await response.json();
    setUser(data);
    setToken(data.token);
    localStorage.setItem('token', data.token);
    return data;
  };

  const register = async (email, password, name, role = 'consumer', extraData = {}) => {
    const response = await fetch(`${API}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name, role, ...extraData })
    });

    if (!response.ok) {
      let msg = 'Registration failed';
      try { const error = await response.json(); msg = error.detail || msg; } catch {}
      throw new Error(msg);
    }

    const data = await response.json();
    setUser(data);
    setToken(data.token);
    localStorage.setItem('token', data.token);
    return data;
  };

  const loginWithGoogle = () => {};
  const processGoogleSession = async () => {};

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const getAuthHeaders = () => {
    const currentToken = token || localStorage.getItem('token');
    return currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {};
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      token,
      login,
      register,
      loginWithGoogle,
      processGoogleSession,
      logout,
      updateUser,
      checkAuth,
      refreshUser: checkAuth,
      getAuthHeaders
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
